import type { BudgetData, BudgetPhaseForm } from "../components/GuidedBudgetEditor";
import { appendBudgetPhaseContiguous } from "./budgetPhaseContiguity";
import {
  initialTwoScheduledPhaseWallRows,
  type ScheduledPhaseWallRow,
} from "./eventWallTime";

export type GuidedBudgetWallRangeParams = Parameters<
  typeof initialTwoScheduledPhaseWallRows
>[0];

/** Phase row as stored in RHF (optional fields) or {@link BudgetData}. */
export type PriorBudgetPhaseForStrategySwitch = {
  time_start?: string;
  time_end?: string;
  amount?: { amount?: unknown; currency?: string };
};

/**
 * Minimal prior state when toggling daily / lifetime / scheduled (RHF values or modal form).
 * Intentionally loose so Yup-inferred form `budget` and {@link BudgetData} both fit.
 */
export type PriorBudgetStateForStrategySwitch = {
  type?: string | null;
  amount?: { amount?: unknown; currency?: string };
  segments?: PriorBudgetPhaseForStrategySwitch[] | null;
};

function budgetCurrencyOrUsd(prev: PriorBudgetStateForStrategySwitch): string {
  return prev.amount?.currency ?? "USD";
}

/** Raw total for simple budgets, or first segment when already scheduled. */
function primaryBudgetAmountRaw(prev: PriorBudgetStateForStrategySwitch): unknown {
  return prev.type === "scheduled"
    ? prev.segments?.[0]?.amount?.amount
    : prev.amount?.amount;
}

function primaryBudgetAmountAsNumber(
  prev: PriorBudgetStateForStrategySwitch
): number | null {
  const raw = primaryBudgetAmountRaw(prev);
  if (raw == null) return null;
  if (typeof raw === "string" && raw.trim() === "") return null;
  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
}

/**
 * When leaving scheduled for daily/lifetime: mirror first phase amount (or top amount) into the simple budget fields.
 */
export function firstSegmentOrTopAmountAsNumber(
  prev: PriorBudgetStateForStrategySwitch
): number | null {
  return primaryBudgetAmountAsNumber(prev);
}

/**
 * Builds phased rows when switching **to** scheduled:
 * - keep existing schedule if there are already ≥2 phases;
 * - if there is one phase with times set, append a second empty-timed phase (amount unset on the new row);
 * - otherwise seed two phases from the flight / schedule wall range: **full** prior amount on phase 1 only.
 */
export function scheduledSegmentsAfterSwitchToScheduled(params: {
  prev: PriorBudgetStateForStrategySwitch;
  wallPair: [ScheduledPhaseWallRow, ScheduledPhaseWallRow];
  firstPhaseAmount: number | null;
  currency: string;
  /** Keeps the final row’s `time_end` on the campaign tail when appending a second phase. */
  scheduleEndWall?: string | null;
}): BudgetPhaseForm[] {
  const { prev, wallPair, firstPhaseAmount, currency, scheduleEndWall } =
    params;
  const [wall1, wall2] = wallPair;
  const curSegs =
    prev.type === "scheduled" && prev.segments?.length
      ? (prev.segments as BudgetPhaseForm[])
      : undefined;

  if (curSegs && curSegs.length >= 2) {
    return curSegs;
  }

  const first = curSegs?.[0];
  if (
    curSegs &&
    curSegs.length === 1 &&
    first &&
    (first.time_start?.trim() || first.time_end?.trim())
  ) {
    return appendBudgetPhaseContiguous(
      curSegs as BudgetPhaseForm[],
      {
        time_start: "",
        time_end: "",
        amount: { amount: null, currency },
      },
      {
        lastPhaseEndWall:
          scheduleEndWall?.trim() ||
          wallPair[1]?.time_end?.trim() ||
          undefined,
      }
    );
  }

  return [
    {
      time_start: wall1.time_start,
      time_end: wall1.time_end,
      amount: { amount: firstPhaseAmount, currency },
    },
    {
      time_start: wall2.time_start,
      time_end: wall2.time_end,
      amount: { amount: null, currency },
    },
  ];
}

export function budgetDataAfterSwitchToScheduled(params: {
  prev: PriorBudgetStateForStrategySwitch;
  wallRangeParams: GuidedBudgetWallRangeParams;
}): BudgetData {
  const { prev, wallRangeParams } = params;
  const currency = budgetCurrencyOrUsd(prev);
  const firstPhaseAmount = primaryBudgetAmountAsNumber(prev);
  const wallPair = initialTwoScheduledPhaseWallRows(wallRangeParams);
  const segments = scheduledSegmentsAfterSwitchToScheduled({
    prev,
    wallPair,
    firstPhaseAmount,
    currency,
    scheduleEndWall: wallRangeParams.scheduleEndWall,
  });

  return {
    type: "scheduled",
    amount: { amount: null, currency },
    base: {
      type: "lifetime",
      amount: { amount: null, currency },
    },
    segments,
  };
}

export function budgetDataAfterSwitchFromScheduled(params: {
  prev: PriorBudgetStateForStrategySwitch;
  nextType: "daily" | "lifetime";
}): BudgetData {
  const { prev, nextType } = params;
  const currency = budgetCurrencyOrUsd(prev);
  const baseAmt = firstSegmentOrTopAmountAsNumber(prev);
  return {
    type: nextType,
    amount: { amount: baseAmt, currency },
    base: {
      type: nextType === "daily" ? "daily" : "lifetime",
      amount: { amount: baseAmt, currency },
    },
    segments: [],
  };
}
